import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CForm,
    CFormInput,
    CRow,
} from "@coreui/react";

import { createExpense } from "../api/expenses";
import { t } from "../i18n";

export default function NewExpensePage() {
    const navigate = useNavigate();

    const [title, setTitle] = useState("");
    const [matchers, setMatchers] = useState([{ title: "" }]);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    function updateMatcher(index, value) {
        setMatchers((currentMatchers) =>
            currentMatchers.map((matcher, matcherIndex) =>
                matcherIndex === index ? { ...matcher, title: value } : matcher
            )
        );
    }

    function addMatcher() {
        setMatchers((currentMatchers) => [
            ...currentMatchers,
            { title: "" },
        ]);
    }

    function removeMatcher(index) {
        setMatchers((currentMatchers) =>
            currentMatchers.filter((_, matcherIndex) => matcherIndex !== index)
        );
    }

    async function handleSubmit(event) {
        event.preventDefault();

        setError(null);
        setSubmitting(true);

        const cleanedMatchers = matchers
            .map((matcher) => ({
                title: matcher.title.trim(),
            }))
            .filter((matcher) => matcher.title.length > 0);

        try {
            await createExpense({
                title: title.trim(),
                matchers: cleanedMatchers,
            });

            navigate("/expenses");
        } catch (submitError) {
            setError(submitError.message);
        } finally {
            setSubmitting(false);
        }
    }

    const canSubmit = title.trim().length > 0 && !submitting;

    return (
        <>
            <div className="mb-4">
                <h1>{t("expenses.new.title")}</h1>
                <p className="text-body-secondary mb-0">
                    {t("expenses.new.description")}
                </p>
            </div>

            <CRow>
                <CCol lg={12}>
                    <CCard>
                        <CCardHeader>
                            <strong>{t("expenses.new.details")}</strong>
                        </CCardHeader>

                        <CCardBody>
                            {error && (
                                <CAlert color="danger">
                                    {error}
                                </CAlert>
                            )}

                            <CForm onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <CFormInput
                                        label={t("expenses.new.titleLabel")}
                                        placeholder={t("expenses.new.titlePlaceholder")}
                                        value={title}
                                        onChange={(event) => setTitle(event.target.value)}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <h5 className="mb-1">{t("expenses.new.matchers")}</h5>
                                            <p className="text-body-secondary mb-0">
                                                {t("expenses.new.matchersDescription")}
                                            </p>
                                        </div>

                                        <CButton
                                            color="secondary"
                                            variant="outline"
                                            type="button"
                                            onClick={addMatcher}
                                        >
                                            {t("expenses.addMatcher")}
                                        </CButton>
                                    </div>

                                    {matchers.map((matcher, index) => (
                                        <div className="d-flex gap-2 mb-2" key={index}>
                                            <CFormInput
                                                placeholder={t("expenses.new.matcherPlaceholder")}
                                                value={matcher.title}
                                                onChange={(event) =>
                                                    updateMatcher(index, event.target.value)
                                                }
                                            />

                                            <CButton
                                                color="danger"
                                                variant="outline"
                                                type="button"
                                                disabled={matchers.length === 1}
                                                onClick={() => removeMatcher(index)}
                                            >
                                                {t("expenses.new.removeMatcher")}
                                            </CButton>
                                        </div>
                                    ))}
                                </div>

                                <div className="d-flex gap-2">
                                    <CButton
                                        color="primary"
                                        type="submit"
                                        disabled={!canSubmit}
                                    >
                                        {submitting ? t("expenses.new.creating") : t("expenses.create")}
                                    </CButton>

                                    <CButton
                                        color="secondary"
                                        variant="outline"
                                        type="button"
                                        onClick={() => navigate("/expenses")}
                                    >
                                        {t("expenses.cancel")}
                                    </CButton>
                                </div>
                            </CForm>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
        </>
    );
}
